/**
 * Deterministic per-page copy generation.
 *
 * Large templated sections (50 state hubs x N games) look like near-duplicate
 * content to search engines when every page repeats the same boilerplate with
 * one noun swapped. This module composes page copy from *real, page-specific
 * data* (state tax rate, game mechanics, draw schedule, ticket price, game
 * counts) and rotates sentence structures with a stable hash of the page slug,
 * so every URL ships a materially different body, intro and FAQ set while
 * staying factually grounded and stable across builds.
 */

import { STATES } from "@/data/states";
import { TAX_RATES } from "@/data/tax-rates";

export function hashSeed(input: string): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

export function pickVariant<T>(seed: string, options: T[], offset = 0): T {
  if (options.length === 0) throw new Error("pickVariant needs at least one option");
  return options[(hashSeed(seed) + offset * 7919) % options.length];
}

/** Stable shuffle so ordered lists (FAQs, fact rows) differ page to page. */
export function rotate<T>(seed: string, items: T[]): T[] {
  if (items.length < 2) return items;
  const start = hashSeed(seed) % items.length;
  return [...items.slice(start), ...items.slice(0, start)];
}

export interface StateFacts {
  name: string;
  abbreviation: string;
  slug: string;
  stateTaxRate: number;
  hasLottery: boolean;
  officialUrl?: string | null;
}

export function getStateFacts(slug: string): StateFacts | null {
  const s = STATES.find((x) => x.slug === slug);
  if (!s) return null;
  const tax = TAX_RATES.find((t) => t.state_id === s.id);
  return {
    name: s.name,
    abbreviation: s.abbreviation,
    slug: s.slug,
    stateTaxRate: tax?.state_tax_rate ?? 0,
    hasLottery: s.has_lottery,
    officialUrl: s.official_lottery_url ?? null,
  };
}

function taxPhrase(f: StateFacts): string {
  return f.stateTaxRate === 0
    ? `${f.name} withholds no state tax on lottery prizes (federal withholding of 24% still applies)`
    : `${f.name} withholds ${f.stateTaxRate}% state tax on lottery prizes on top of the 24% federal rate`;
}

/* ------------------------------------------------------------------ */
/* State hub pages                                                     */
/* ------------------------------------------------------------------ */

export function stateIntro(f: StateFacts, gameCount: number): string {
  const count = gameCount > 0 ? `${gameCount} ` : "";
  const a = /^[AEIOU]/.test(f.name) ? "an" : "a";
  const variants = [
    `Every ${f.name} draw game is tracked here — ${count}titles in total, from ${f.abbreviation} in-state draws to the multi-state Powerball and Mega Millions games sold at ${f.name} retailers. Winning numbers post as soon as the official draw is certified, and ${taxPhrase(f)}.`,
    `This is the live scoreboard for the ${f.name} Lottery: ${count}draw games with the latest winning numbers, current jackpot estimates and the exact days each game is drawn. Prize planning tip — ${taxPhrase(f)}.`,
    `Looking up a ${f.abbreviation} ticket? Start with the table below: it lists the most recent draw for each ${f.name} game, followed by ${count}game pages with full history, hot and cold numbers and payout details. Remember that ${taxPhrase(f)}.`,
    `${f.name} players get results for ${count}games in one place — in-state draws plus the national jackpot games available at ${f.abbreviation} retailers. Numbers refresh after each certified drawing, and ${taxPhrase(f)}.`,
    `Check ${a} ${f.name} ticket in seconds. Below you'll find the newest winning numbers for each of the ${count}games offered in ${f.abbreviation}, their draw schedules and jackpot estimates. On the tax side, ${taxPhrase(f)}.`,
  ];
  return pickVariant(f.slug, variants);
}

export function stateMetaDescription(f: StateFacts): string {
  const variants = [
    `Today's ${f.name} Lottery winning numbers, jackpots and draw times — plus Powerball and Mega Millions results for ${f.abbreviation} players and ${f.stateTaxRate === 0 ? "no state tax on prizes" : `${f.stateTaxRate}% ${f.abbreviation} prize tax`}.`,
    `See the latest ${f.name} lottery results the moment each draw is certified: winning numbers, jackpot estimates, draw schedules and past ${f.abbreviation} numbers for every game.`,
    `${f.name} lottery results hub — check ${f.abbreviation} tickets against tonight's winning numbers, browse past draws, and see jackpots and tax withholding for ${f.name} winners.`,
    `Winning numbers for every ${f.name} Lottery game, updated after each official draw, with jackpots, drawing days and a full results archive for ${f.abbreviation} players.`,
  ];
  return pickVariant(`${f.slug}-meta`, variants);
}

export function stateTitle(f: StateFacts): string {
  const variants = [
    `${f.name} Lottery Results & Winning Numbers`,
    `${f.name} Lottery Winning Numbers & Jackpots`,
    `${f.name} Lottery Results Today & Draw Times`,
    `${f.name} (${f.abbreviation}) Lottery Results & Numbers`,
  ];
  const base = pickVariant(`${f.slug}-title`, variants);
  const suffix = " | USA Lottery Club";
  return base.length + suffix.length <= 60 ? `${base}${suffix}` : base;
}

export function stateFactRows(f: StateFacts, gameCount: number): Array<{ label: string; value: string }> {
  const rows = [
    { label: "State tax withheld on prizes", value: f.stateTaxRate === 0 ? "None" : `${f.stateTaxRate}%` },
    { label: "Federal tax withheld", value: "24% on prizes over $5,000" },
    { label: "Draw games tracked", value: gameCount > 0 ? String(gameCount) : "Updating" },
    { label: "Multi-state games sold", value: "Powerball, Mega Millions" },
  ];
  return rotate(`${f.slug}-facts`, rows);
}

/* ------------------------------------------------------------------ */
/* Game and state-game pages                                           */
/* ------------------------------------------------------------------ */

export interface GameCopyInput {
  name: string;
  slug: string;
  stateName?: string;
  stateSlug?: string;
  drawSchedule?: string | null;
  ticketPrice?: number | null;
  mainCount?: number | null;
  mainRange?: number | null;
  bonusCount?: number | null;
  bonusRange?: number | null;
  oddsInfo?: string | null;
  isMultistate?: boolean;
}

function mechanics(g: GameCopyInput): string {
  if (!g.mainCount || !g.mainRange) return "";
  const main = `${g.mainCount} number${g.mainCount === 1 ? "" : "s"} from 1–${g.mainRange}`;
  const bonus = g.bonusCount && g.bonusRange ? ` plus ${g.bonusCount} bonus ball from 1–${g.bonusRange}` : "";
  return `${main}${bonus}`;
}

export function gameIntro(g: GameCopyInput): string {
  const seed = `${g.stateSlug ?? "us"}-${g.slug}`;
  const where = g.stateName ? ` in ${g.stateName}` : "";
  const scope = g.stateName ? `${g.stateName} ${g.name}` : g.name;
  const m = mechanics(g);
  const price = g.ticketPrice ? `$${Number(g.ticketPrice).toFixed(2)} per play` : "";
  const sched = g.drawSchedule ? `Draws are held ${g.drawSchedule}.` : "";
  const odds = g.oddsInfo ? ` ${g.oddsInfo}.` : "";

  const variants = [
    `${scope} results land here minutes after each official drawing. ${m ? `A standard ticket covers ${m}${price ? `, ${price}` : ""}. ` : ""}${sched}${odds}`,
    `Checking a ${scope} ticket${where ? "" : ""}? The newest winning numbers sit at the top of this page, with the last 10 draws below. ${m ? `${g.name} asks you to match ${m}${price ? ` for ${price}` : ""}. ` : ""}${sched}${odds}`,
    `${sched} This page tracks every ${scope} draw — latest numbers, jackpot movement, hot and cold trends and a searchable archive.${m ? ` Play by choosing ${m}${price ? ` at ${price}` : ""}.` : ""}${odds}`,
    `${m ? `Match ${m} to win${price ? `, ${price}` : ""}. ` : ""}Below you'll find the current ${scope} winning numbers, the previous draw for comparison and the full results history.${sched ? ` ${sched}` : ""}${odds}`,
  ];
  return pickVariant(seed, variants).replace(/\s+/g, " ").trim();
}

export function gameMetaDescription(g: GameCopyInput): string {
  const seed = `${g.stateSlug ?? "us"}-${g.slug}-meta`;
  const scope = g.stateName ? `${g.stateName} ${g.name}` : g.name;
  const sched = g.drawSchedule ? ` Drawn ${g.drawSchedule}.` : "";
  const variants = [
    `Tonight's ${scope} winning numbers, jackpot estimate and full draw archive.${sched} Updated after every certified drawing.`,
    `Check your ${scope} ticket: latest winning numbers, previous draw, hot and cold numbers and past results.${sched}`,
    `${scope} results and jackpot tracker — see the newest numbers, prize breakdown pointers and every past draw in one archive.${sched}`,
    `Latest ${scope} numbers, payout tools and drawing schedule for players following ${g.name} results.${sched}`,
  ];
  return pickVariant(seed, variants).replace(/\s+/g, " ").trim();
}

export function gameTitle(g: GameCopyInput): string {
  const seed = `${g.stateSlug ?? "us"}-${g.slug}-title`;
  const scope = g.stateName ? `${g.stateName} ${g.name}` : g.name;
  const variants = [
    `${scope} Winning Numbers & Results Today`,
    `${scope} Results — Latest Winning Numbers & Jackpot`,
    `${scope} Numbers Today: Results, Jackpot & Past Draws`,
    `${scope} Winning Numbers, Draw Times & Results History`,
  ];
  return `${pickVariant(seed, variants)} | USA Lottery Club`;
}

export function buildGameFaqs(g: GameCopyInput): Array<{ q: string; a: string }> {
  const scope = g.stateName ? `${g.stateName} ${g.name}` : g.name;
  const where = g.stateName ? ` in ${g.stateName}` : "";
  const facts = g.stateSlug ? getStateFacts(g.stateSlug) : null;
  const m = mechanics(g);

  const faqs: Array<{ q: string; a: string }> = [
    {
      q: `What are tonight's ${scope} winning numbers?`,
      a: `The newest ${scope} numbers appear at the top of this page and are replaced automatically once the next official draw is certified.`,
    },
    {
      q: `When is ${g.name} drawn${where}?`,
      a: g.drawSchedule
        ? `${g.name} is drawn ${g.drawSchedule}. Ticket sales usually close shortly before the draw, so buy early on drawing days.`
        : `${g.name} draw days and cut-off times are shown in the schedule near the top of this page.`,
    },
    {
      q: `How do you play ${g.name}?`,
      a: m
        ? `Pick ${m}${g.ticketPrice ? ` and pay $${Number(g.ticketPrice).toFixed(2)} per play` : ""}. You can choose your own numbers or take a quick pick from the terminal.`
        : `Choose your numbers at any licensed retailer, or ask for a quick pick to have them selected for you.`,
    },
    {
      q: `What tax is withheld on ${scope} prizes?`,
      a: facts
        ? `${taxPhrase(facts)}. Use our payout calculator to estimate a lump-sum or annuity total after withholding.`
        : `Federal withholding is 24% on prizes over $5,000; state withholding depends on where the ticket was bought.`,
    },
    {
      q: `Where can I see past ${scope} results?`,
      a: `The Results History table lists the most recent draws, and the full history page holds every archived ${g.name} number set with dates.`,
    },
    {
      q: `Do hot and cold numbers improve ${g.name} odds?`,
      a: `No — each draw is independent, so frequency data is for interest only.${g.oddsInfo ? ` ${g.oddsInfo}.` : ""} Many players still use the trends section above to build a line.`,
    },
    {
      q: `How long do I have to claim a ${scope} prize?`,
      a: `Claim windows are set by the lottery that sold the ticket${facts?.officialUrl ? ` — confirm the deadline on the official ${facts.name} Lottery website linked on this page` : ""}. Sign the back of your ticket as soon as you buy it.`,
    },
  ];
  return rotate(`${g.stateSlug ?? "us"}-${g.slug}-faq`, faqs).slice(0, 6);
}

/* ------------------------------------------------------------------ */
/* State hub FAQs                                                      */
/* ------------------------------------------------------------------ */

export function buildStateFaqs(f: StateFacts, gameCount = 0): Array<{ q: string; a: string }> {
  const count = gameCount > 0 ? `${gameCount} draw games` : "every draw game";
  const faqs: Array<{ q: string; a: string }> = [
    {
      q: `What are today's ${f.name} lottery winning numbers?`,
      a: `The table near the top of this page shows the most recent certified draw for ${count} sold in ${f.name}. Numbers update automatically after each official ${f.abbreviation} drawing.`,
    },
    {
      q: `Which lottery games can I play in ${f.name}?`,
      a: f.hasLottery
        ? `${f.name} players can buy in-state draw games plus the multi-state Powerball and Mega Millions tickets at licensed ${f.abbreviation} retailers. Every game we track is listed below with its draw schedule.`
        : `${f.name} does not operate a state lottery, so tickets must be bought in a neighbouring state that does.`,
    },
    {
      q: `How much tax is withheld on ${f.name} lottery prizes?`,
      a: `${taxPhrase(f)}. Our payout calculator estimates your take-home for a lump sum or annuity.`,
    },
    {
      q: `What time are ${f.name} lottery drawings held?`,
      a: `Each game has its own draw days and cut-off time — they are shown on the game cards below and on each ${f.abbreviation} game page.`,
    },
    {
      q: `Where do I claim a winning ${f.name} lottery ticket?`,
      a: f.officialUrl
        ? `Small prizes are paid at retailers; larger prizes are claimed through the ${f.name} Lottery, whose official site is linked at the bottom of this page. Sign the back of your ticket immediately.`
        : `Sign the back of your ticket and follow the claim process published by the lottery that sold it. Retailers pay smaller prizes; larger prizes go through a lottery claim centre.`,
    },
    {
      q: `Can I check old ${f.name} winning numbers?`,
      a: `Yes — each ${f.abbreviation} game page keeps a searchable results archive, and the results hub lists every draw across all states by date.`,
    },
  ];
  return rotate(`${f.slug}-state-faq`, faqs).slice(0, 5);
}

/* ---------------------------------------------------------------------------
 * Instant (scratch-off) game pages
 *
 * There are thousands of these URLs across 40+ states. Titles and descriptions
 * are composed from the game's own facts (price, top prize, category, state)
 * and rotated with a stable slug hash so no two pages ship the same snippet.
 * ------------------------------------------------------------------------- */

export interface InstantGameCopyInput {
  name: string;
  slug: string;
  stateName: string;
  stateSlug: string;
  topPrize?: string | null;
  priceRange?: string | null;
  categories?: string[];
}

const clamp = (s: string, max: number) =>
  s.length <= max ? s : `${s.slice(0, max - 1).replace(/[\s,.;:—-]+$/, "")}…`;

/** Placeholder feed values ("Varies", "Not Available", "-") are not facts. */
function realValue(v?: string | null): string | undefined {
  const s = (v ?? "").trim();
  if (!s || !/\d/.test(s)) return undefined;
  if (/not available|varies|unknown|n\/a/i.test(s)) return undefined;
  return s;
}

export function instantGameTitle(g: InstantGameCopyInput): string {
  const seed = `${g.stateSlug}-${g.slug}-ig-title`;
  const prize = realValue(g.topPrize);
  const price = realValue(g.priceRange);
  const variants = [
    `${g.name} ${g.stateName} Scratch-Off${prize ? ` — Top Prize ${prize}` : ""}`,
    `${g.stateName} ${g.name} Scratch Ticket${price ? ` (${price})` : ""}: Prizes & Odds`,
    `${g.name} Scratch-Off (${g.stateName})${prize ? ` — Win Up To ${prize}` : ""}`,
    `${g.name} — ${g.stateName} Lottery Instant Game${price ? `, ${price}` : ""}`,
  ];
  // Prefer a variant that fits in the SERP title budget over a clamped one.
  const start = hashSeed(seed) % variants.length;
  for (let i = 0; i < variants.length; i++) {
    const candidate = variants[(start + i) % variants.length];
    if (candidate.length <= 62) return candidate;
  }
  return clamp(variants[start], 62);
}

export function instantGameMetaDescription(g: InstantGameCopyInput): string {
  const seed = `${g.stateSlug}-${g.slug}-ig-desc`;
  const prize = realValue(g.topPrize);
  const price = realValue(g.priceRange);
  const cat = g.categories?.[0]?.trim();
  const prizeBit = prize ? ` Top prize ${prize}.` : "";
  const priceBit = price ? ` Tickets ${price}.` : "";
  const catBit = cat ? ` ${cat} style ticket.` : "";
  const variants = [
    `${g.name} is a ${g.stateName} Lottery scratch-off game.${prizeBit}${priceBit} See prizes, how to play and where to buy.`,
    `Everything about the ${g.stateName} ${g.name} scratch ticket:${prizeBit}${priceBit}${catBit} Plus similar ${g.stateName} instant games.`,
    `Play ${g.name} in ${g.stateName}?${priceBit}${prizeBit} Check the prize structure, game details and other ${g.stateName} scratch-offs worth playing.`,
    `${g.stateName} ${g.name} instant game guide.${prizeBit}${priceBit} Game details, FAQs and related ${g.stateName} scratch-offs.`,
  ];
  return clamp(pickVariant(seed, variants).replace(/\s+/g, " ").trim(), 158);
}
